import { useCallback, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Link as RouterLink } from 'react-router-dom';
import Text from 'common/ui/Text';
import { Colors, TextCss } from 'styles';
import styled, { css } from 'styled-components';
import Input from 'common/ui/Input';
import { Button } from 'common/ui/Button';
import { Stack } from 'common/ui/Layout';
import { AppRoutePath } from 'config/constants';
import { passwordResetSuccessfulState } from 'config/recoilState';
import { useRecoilState } from 'recoil';
import GenericSnackBar from '../GenericSnackBar/GenericSnackBar';
import AuthLayout from '../Layouts/Auth/AuthLayout';
import { useAuthContext } from 'hooks/useAuth';
import { PWATitleBar } from 'common/ui/PWATitleBar';
// @ts-expect-error [EN-7967] - TS2307 - Cannot find module 'formik/@Field' or its corresponding type declarations.
import type { FieldConfig } from 'formik/@Field';
import IconButton from '@material-ui/core/IconButton';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Visibility from '@material-ui/icons/Visibility';

const InputCSS = css`
  display: inline-block;
  margin-top: 0.2rem;
  ::placeholder {
    color: ${Colors.gray10};
    font-size: 14px;
  }
  width: 100%;
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: ${Colors.gray10};
    transition: background-color 5000s ease-in-out 0s;
    box-shadow: inset 0 0 20px 20px ${Colors.gray4};
  }
`;

const FieldWrapper = (props: FieldConfig<string>) => <Field {...props} />;

const Link = styled(RouterLink)`
  color: ${Colors.gray9};
  text-decoration: underline;
  ${TextCss.display1}
`;

function LoginComponent(): React.ReactElement {
  const { login, error: authenticateError } = useAuthContext();
  const onSubmit = useCallback(
    // @ts-expect-error [EN-10263] - TS7031 - Binding element 'email' implicitly has an 'any' type. | TS7031 - Binding element 'password' implicitly has an 'any' type. | TS7031 - Binding element 'setSubmitting' implicitly has an 'any' type.
    async ({ email, password }, { setSubmitting }) => {
      setSubmitting(true);
      try {
        await login({ email, password });
      } finally {
        setSubmitting(false);
      }
    },
    [login]
  );

  const [passwordResetSuccessful, setPasswordResetSuccessful] = useRecoilState(
    passwordResetSuccessfulState
  );
  const [formError, setFormError] = useState({ email: '' });
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      <PWATitleBar />
      <GenericSnackBar
        open={passwordResetSuccessful}
        severity="success"
        msg="Password has been successfully reset."
        handleClose={() => setPasswordResetSuccessful(false)}
      />
      <AuthLayout title="Welcome back">
        <img
          src="/logos/logoMain.png"
          alt="Sirona Medial Logo"
          css={`
            max-width: 243.75px;
            max-height: 65px;
            margin-bottom: 10px;
          `}
        />

        <Formik
          initialValues={{ email: '', password: '' }}
          validate={(values) => {
            const errors = { email: '' };
            if (values.email === '' || values.email == null) {
              errors.email = 'Required';
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
              errors.email = 'Invalid email address';
            }
            setFormError(errors);
          }}
          onSubmit={onSubmit}
        >
          {({ submitForm, isSubmitting, touched }) => (
            // @ts-expect-error [EN-7967] - TS2739 - Type '{ children: Element; css: string; }' is missing the following properties from type 'Pick<DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>, "name" | "action" | ... 260 more ... | "onPointerLeaveCapture">': placeholder, onPointerEnterCapture, onPointerLeaveCapture
            <Form
              css={`
                width: 100%;
                margin-top: 2rem;
              `}
            >
              <Stack space="xsmall" vertical stretchX>
                <div>
                  <label
                    htmlFor="email"
                    css={`
                      margin-left: 1.5rem;
                      font-size: 12px;
                    `}
                  >
                    Email address
                  </label>
                  <Input
                    as={FieldWrapper}
                    id="email"
                    type="email"
                    label="Email"
                    name="email"
                    variant="light"
                    placeholder="eg: johndoe@gmail.com"
                    // @ts-expect-error [EN-10263] - TS7006 - Parameter 'e' implicitly has an 'any' type.
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        submitForm();
                        e.stopPropagation();
                      }
                    }}
                    css={InputCSS}
                  />
                  {formError.email != null && formError.email !== '' && touched.email && (
                    <Text
                      variant="caption"
                      css={`
                        color: ${Colors.red6};
                        margin-left: 12px;
                        margin-top: 1rem;
                      `}
                    >
                      {formError.email}
                    </Text>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="password"
                    css={`
                      margin-left: 1.5rem;
                      font-size: 12px;
                    `}
                  >
                    Password
                  </label>
                  <Stack
                    alignY="center"
                    css={`
                      position: relative;
                    `}
                  >
                    <Input
                      as={FieldWrapper}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      label="Password"
                      name="password"
                      variant="light"
                      placeholder="Enter your password"
                      // @ts-expect-error [EN-10263] - TS7006 - Parameter 'e' implicitly has an 'any' type.
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          submitForm();
                          e.stopPropagation();
                        }
                      }}
                      css={InputCSS}
                    />
                    <IconButton
                      disableRipple
                      aria-label="toggle password visibility"
                      data-testid={`toggle-password-${showPassword ? 'hidden' : 'visible'}`}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      css={`
                        position: absolute;
                        height: 16px;
                        width: 16px;
                        right: 12px;
                      `}
                    >
                      {showPassword ? (
                        <Visibility
                          css={`
                            font-size: 16px;
                            color: ${Colors.gray9};
                          `}
                        />
                      ) : (
                        <VisibilityOff
                          css={`
                            font-size: 16px;
                            color: ${Colors.gray9};
                          `}
                        />
                      )}
                    </IconButton>
                  </Stack>
                </div>

                <div>
                  <Link
                    to={AppRoutePath.RESET_PASSWORD}
                    css={`
                      margin-left: 1.5rem;
                      padding: 0;
                      text-transform: none;
                      font-size: 12px;
                    `}
                  >
                    <Text color="secondary">Reset password</Text>
                  </Link>
                </div>
                <Stack alignX="center" css="margin-top: 2rem" vertical>
                  {isSubmitting ? (
                    <CircularProgress />
                  ) : (
                    <Stack css="width: 100%" alignX="center" alignY="center">
                      <Button
                        disabled={formError.email != null && formError.email !== ''}
                        css={`
                          width: 100%;
                        `}
                        type="submit"
                      >
                        Log me in
                      </Button>
                    </Stack>
                  )}
                  {authenticateError != null && (
                    <Text
                      variant="caption"
                      css={`
                        color: ${Colors.red6};
                        margin-top: 1rem;
                      `}
                    >
                      Authentication error. Please try again.
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </AuthLayout>
    </>
  );
}

export default LoginComponent;
